import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a date in a user-friendly way
 * @param date The date to format
 * @returns A formatted date string (e.g., "July 25, 2025")
 */
export function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Title-cases a string: lowercases the input, splits on whitespace/underscore/
 * hyphen, drops empty segments, and capitalizes each remaining word. Examples:
 * `"HIGH_CONFIDENCE"` → `"High Confidence"`, `"call_note"` → `"Call Note"`.
 */
export function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Convert a proto enum JSON value to a human-readable label: drops the given
 * prefix (e.g. `"SEVERITY_"`, `"CONFIDENCE_"`) and title-cases the remainder.
 * `"SEVERITY_HIGH"` with prefix `"SEVERITY_"` → `"High"`.
 */
export function formatEnumLabel(raw: string, prefix: string): string {
  const suffix = raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
  return toTitleCase(suffix);
}

/**
 * Trim and clamp a free-form string to a short display summary (~90 chars).
 * Returns `undefined` when the input is empty/whitespace.
 */
export function summarize(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (trimmed.length <= 90) return trimmed;
  return `${trimmed.slice(0, 87).trimEnd()}...`;
}
